import React, { useState, useRef, useEffect, useCallback } from "react";
import { Blurhash } from "react-blurhash";
import LargePlayerIcon from "@/assets/icons/LargePlayerIcon.svg";
import {
  Volume2,
  VolumeX,
  Maximize,
  X,
  Play,
  Pause,
  RotateCcw,
} from "lucide-react";
import { createPortal } from "react-dom";

// Default blurhash to use while loading
const DEFAULT_BLURHASH = "UGF=Hx~VD$NG0Kf4-;x]yY%MD%V@VDM{=|s:";

type TVideoPlayerProps = {
  url: string;
  thumbnailUrl?: string;
  flag?: React.ReactNode;
  language?: string;
  large?: boolean;
  width?: string;
  height?: string;
  title?: string;
  icon?: React.ReactNode;
  className?: string;
  blurhash?: string;
  currentlyPlaying: string | null;
  setCurrentlyPlaying: (url: string | null) => void;
  pauseIconPosition?: "top" | "bottom";
  showFullscreenButton?: boolean;
};

const VideoPlayerV3: React.FC<TVideoPlayerProps> = ({
  url,
  thumbnailUrl,
  flag,
  language,
  title,
  large,
  icon,
  className,
  blurhash = DEFAULT_BLURHASH,
  currentlyPlaying,
  setCurrentlyPlaying,
  pauseIconPosition = "bottom",
  showFullscreenButton = true,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const fullscreenVideoRef = useRef<HTMLVideoElement>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // States
  const [muted, setMuted] = useState(true);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isThumbnailLoaded, setIsThumbnailLoaded] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [hasEnded, setHasEnded] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenMuted, setFullscreenMuted] = useState(true);
  const [fullscreenPlaying, setFullscreenPlaying] = useState(false);
  const [fullscreenEnded, setFullscreenEnded] = useState(false);
  const [showFullscreenControls, setShowFullscreenControls] = useState(true);
  const [isThumbnailError, setIsThumbnailError] = useState(false);

  const isPlaying = currentlyPlaying === url;
  const iconSize = large ? "16" : "14";
  // At the top: computed flag
  const hasValidThumbnail = !!thumbnailUrl && !isThumbnailError;

  // Update shouldShowThumbnail logic
  const shouldShowThumbnail =
    hasValidThumbnail && (!isPlaying || !isVideoLoaded || hasEnded);

  // Handle play/pause toggling
  const handlePlay = useCallback(() => {
    if (hasEnded) {
      setHasEnded(false);
      setCurrentlyPlaying(url);
    } else if (isPlaying) {
      setCurrentlyPlaying(null);
    } else {
      setCurrentlyPlaying(url);
    }
  }, [hasEnded, isPlaying, setCurrentlyPlaying, url]);

  // Control video play/pause based on isPlaying state
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying && !isFullscreen) {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.error("Error playing video:", error);
        });
      }
    } else if (!isFullscreen) {
      video.pause();
    }
  }, [isPlaying, isFullscreen]);

  // Control fullscreen video
  useEffect(() => {
    const video = fullscreenVideoRef.current;
    if (!video) return;

    if (isFullscreen && fullscreenPlaying && !fullscreenEnded) {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.error("Error playing fullscreen video:", error);
        });
      }
    } else {
      video.pause();
    }
  }, [isFullscreen, fullscreenPlaying, fullscreenEnded]);

  // Handle escape key and prevent body scroll
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isFullscreen) {
        handleCloseFullscreen();
      }
    };

    if (isFullscreen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";

      return () => {
        document.removeEventListener("keydown", handleEscape);
        document.body.style.overflow = "";
      };
    }
  }, [isFullscreen]);

  // Auto-hide fullscreen controls
  useEffect(() => {
    if (!isFullscreen) return;

    const hideControls = () => {
      if (fullscreenPlaying && !fullscreenEnded) {
        setShowFullscreenControls(false);
      }
    };

    // Show controls initially and set timeout to hide them
    setShowFullscreenControls(true);

    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }

    controlsTimeoutRef.current = setTimeout(hideControls, 3000);

    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [isFullscreen, fullscreenPlaying, fullscreenEnded]);

  // Show controls when video is paused or ended
  useEffect(() => {
    if (isFullscreen && (!fullscreenPlaying || fullscreenEnded)) {
      setShowFullscreenControls(true);
    }
  }, [isFullscreen, fullscreenPlaying, fullscreenEnded]);

  // Memoized event handlers
  const handleVideoCanPlay = useCallback(() => {
    setIsVideoLoaded(true);
    setIsBuffering(false);
  }, []);

  const handleVideoWaiting = useCallback(() => {
    setIsBuffering(true);
  }, []);

  const handleVideoPlaying = useCallback(() => {
    setIsBuffering(false);
  }, []);

  const handleVideoEnded = useCallback(() => {
    setHasEnded(true);
    setCurrentlyPlaying(null);
  }, [setCurrentlyPlaying]);

  const handleFullscreenVideoEnded = useCallback(() => {
    setFullscreenEnded(true);
    setFullscreenPlaying(false);
  }, []);

  const handleVideoError = useCallback(() => {
    setIsBuffering(false);
  }, []);

  const handleMute = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setMuted((prev) => !prev);
  }, []);

  const handleFullscreenMute = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setFullscreenMuted((prev) => !prev);
  }, []);

  const handleFullscreenPlayPause = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      if (fullscreenEnded) {
        // Restart video
        if (fullscreenVideoRef.current) {
          fullscreenVideoRef.current.currentTime = 0;
          setFullscreenEnded(false);
          setFullscreenPlaying(true);
        }
      } else {
        setFullscreenPlaying((prev) => !prev);
      }

      // Reset controls timeout
      setShowFullscreenControls(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
        controlsTimeoutRef.current = setTimeout(() => {
          if (fullscreenPlaying && !fullscreenEnded) {
            setShowFullscreenControls(false);
          }
        }, 3000);
      }
    },
    [fullscreenEnded, fullscreenPlaying]
  );

  const handleFullscreen = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsFullscreen(true);
      setFullscreenPlaying(isPlaying);
      setFullscreenEnded(false);
    },
    [isPlaying]
  );

  const handleCloseFullscreen = useCallback(() => {
    setIsFullscreen(false);
    setFullscreenPlaying(false);
    setFullscreenEnded(false);

    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
  }, []);

  const handleFullscreenBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        handleCloseFullscreen();
      }
    },
    [handleCloseFullscreen]
  );

  const handleFullscreenMouseMove = useCallback(() => {
    setShowFullscreenControls(true);

    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }

    controlsTimeoutRef.current = setTimeout(() => {
      if (fullscreenPlaying && !fullscreenEnded) {
        setShowFullscreenControls(false);
      }
    }, 3000);
  }, [fullscreenPlaying, fullscreenEnded]);

  return (
    <>
      <div
        onClick={handlePlay}
        className={`player-wrapper cursor-pointer w-full h-full relative overflow-hidden ${className}`}
      >
        {/* Language or title label */}
        <div className="absolute top-2.5 right-2.5 z-[49]">
          {language && flag && (
            <div className="flex gap-x-2 items-center px-2 py-1 bg-white/95 backdrop-blur-sm border border-[#FF6600] rounded-[30px] text-[#000000] font-medium text-xs tracking-[-1.5%] font-grotesk shadow-sm">
              {flag}
              {language}
            </div>
          )}
          {title && (
            <div className="flex items-center gap-1 py-[2px] px-[6px] bg-white/95 backdrop-blur-sm text-[8px] text-black font-medium leading-3 tracking-[-.75%] rounded-full border-[1.5px] border-[#EEEEEE] whitespace-nowrap shadow-sm">
              {title}
            </div>
          )}
        </div>
        {/* Blurhash placeholder - only show if expecting thumbnail */}
        {hasValidThumbnail && !isThumbnailLoaded && (
          <div className="absolute inset-0 w-full h-full z-10">
            <Blurhash
              hash={blurhash}
              width="100%"
              height="100%"
              resolutionX={32}
              resolutionY={32}
              punch={1}
            />
          </div>
        )}

        {/* Thumbnail image */}
        {hasValidThumbnail && (
          <img
            src={thumbnailUrl}
            alt="Video thumbnail"
            className={`absolute inset-0 w-full h-full object-cover z-20 transition-opacity duration-300 ${
              isThumbnailLoaded ? "opacity-100" : "opacity-0"
            } ${shouldShowThumbnail ? "visible" : "invisible"}`}
            onLoad={() => setIsThumbnailLoaded(true)}
            onError={() => setIsThumbnailError(true)}
          />
        )}

        {/* Video element */}
        <video
          ref={videoRef}
          className={`absolute inset-0 w-full h-full object-cover z-30 transition-opacity duration-300 ${
            (isPlaying && isVideoLoaded) || !hasValidThumbnail
              ? "opacity-100"
              : "opacity-0"
          }`}
          src={url}
          playsInline
          muted={muted}
          preload="metadata"
          onLoadedData={() => {
            if (isThumbnailError && videoRef.current) {
              videoRef.current.currentTime = 0;
            }
          }}
          onWaiting={handleVideoWaiting}
          onCanPlay={handleVideoCanPlay}
          onPlaying={handleVideoPlaying}
          onEnded={handleVideoEnded}
          onError={handleVideoError}
        />

        {/* Play button overlay */}
        {shouldShowThumbnail && (
          <div
            className="absolute inset-0 flex items-center justify-center z-40"
            onClick={handlePlay}
          >
            <div className="transition-all duration-200 hover:scale-105">
              <LargePlayerIcon />
            </div>
          </div>
        )}

        {/* Loading spinner */}
        {isBuffering && (
          <div className="absolute inset-0 flex items-center justify-center z-50">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-white/30 border-t-white"></div>
          </div>
        )}

        {/* Control buttons */}
        {isPlaying && (
          <div className="absolute w-full flex items-center justify-between px-4 bottom-4 left-0 z-50">
            <button
              aria-label="mute-button"
              className="text-white hover:scale-110 transition-transform duration-200"
              onClick={handleMute}
            >
              <div className="bg-black/40 backdrop-blur-sm p-2 rounded-full hover:bg-black/60 transition-colors duration-200">
                {muted ? (
                  <VolumeX size={iconSize} />
                ) : (
                  <Volume2 size={iconSize} />
                )}
              </div>
            </button>

            {showFullscreenButton && (
              <button
                aria-label="fullscreen-button"
                className="text-white hover:scale-110 transition-transform duration-200"
                onClick={handleFullscreen}
              >
                <div className="bg-black/40 backdrop-blur-sm p-2 rounded-full hover:bg-black/60 transition-colors duration-200">
                  <Maximize size={iconSize} />
                </div>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Fullscreen Modal */}
      {isFullscreen &&
        createPortal(
          <div
            className="fixed inset-0 bg-black z-[9999] flex items-center justify-center"
            onClick={handleFullscreenBackdropClick}
            onMouseMove={handleFullscreenMouseMove}
            style={{ touchAction: "none" }}
          >
            <div className="relative w-full h-full flex items-center justify-center">
              {/* Fullscreen Video */}
              <video
                ref={fullscreenVideoRef}
                className="w-full h-full object-contain"
                src={url}
                playsInline
                muted={fullscreenMuted}
                controls={false}
                onEnded={handleFullscreenVideoEnded}
                autoPlay
                onClick={handleFullscreenPlayPause}
              />

              {/* Center play/pause/replay button overlay */}
              {(!fullscreenPlaying || fullscreenEnded) && (
                <div
                  className="absolute inset-0 flex items-center justify-center cursor-pointer z-40"
                  onClick={handleFullscreenPlayPause}
                >
                  <div className="bg-black/50 backdrop-blur-sm rounded-full p-8 hover:bg-black/70 transition-all duration-300 hover:scale-110">
                    {fullscreenEnded ? (
                      <RotateCcw size="48" className="text-white" />
                    ) : (
                      <Play size="48" className="text-white ml-2" />
                    )}
                  </div>
                </div>
              )}

              {/* Top controls bar */}
              <div
                className={`absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 to-transparent p-6 transition-opacity duration-300 z-50 ${
                  showFullscreenControls
                    ? "opacity-100"
                    : "opacity-0 pointer-events-none"
                }`}
              >
                <div className="flex items-center justify-between">
                  {/* Title/Language */}
                  <div className="flex items-center space-x-3">
                    {language && flag && (
                      <div className="flex gap-x-2 items-center px-3 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white font-medium text-sm">
                        {flag}
                        {language}
                      </div>
                    )}
                    {title && !language && (
                      <div className="px-3 py-2 bg-white/10 backdrop-blur-md text-sm text-white font-medium rounded-full border border-white/20 whitespace-nowrap">
                        {title}
                      </div>
                    )}
                  </div>

                  {/* Close button */}
                  <button
                    onClick={handleCloseFullscreen}
                    className="text-white hover:scale-110 transition-transform duration-200"
                  >
                    <div className="bg-black/40 backdrop-blur-sm p-2 rounded-full hover:bg-black/60 transition-colors duration-200">
                      <X size="20" />
                    </div>
                  </button>
                </div>
              </div>

              {/* Bottom controls (volume, play/pause) */}
              <div
                className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 transition-opacity duration-300 z-50 ${
                  showFullscreenControls
                    ? "opacity-100"
                    : "opacity-0 pointer-events-none"
                }`}
              >
                <div className="flex items-center justify-center space-x-4">
                  {/* Volume toggle */}
                  <button
                    onClick={handleFullscreenMute}
                    className="text-white hover:scale-110 transition-transform duration-200"
                  >
                    <div className="bg-black/40 backdrop-blur-sm p-2 rounded-full hover:bg-black/60 transition-colors duration-200">
                      {fullscreenMuted ? (
                        <VolumeX size="20" />
                      ) : (
                        <Volume2 size="20" />
                      )}
                    </div>
                  </button>

                  {/* Play/Pause toggle */}
                  <button
                    onClick={handleFullscreenPlayPause}
                    className="text-white hover:scale-110 transition-transform duration-200"
                  >
                    <div className="bg-black/40 backdrop-blur-sm p-2 rounded-full hover:bg-black/60 transition-colors duration-200">
                      {fullscreenPlaying ? (
                        <Pause size="20" />
                      ) : (
                        <Play size="20" />
                      )}
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default React.memo(VideoPlayerV3);
